<script setup lang="ts">
import { computed } from 'vue';
import type { ApplicationDetail } from '@/entities/application/model/types';
import type { LuggageLocationResponse } from '@/entities/application/api/fetchLuggageLocation';
import type { components } from '@/shared/api/autogen/schema';
import VExpansionPanel from '@/shared/ui/VExpansionPanel.vue';
import InfoPanel from './InfoPanel.vue';
import LocationHistoryPanel from '@/entities/application/ui/LocationHistoryPanel.vue';
import LuggageInfoPanel from '@/entities/application/ui/LuggageInfoPanel.vue';
import QrCode from '@/entities/application/ui/QrCode.vue';
import { buildInfoItemForUserInfo, buildInfoItemForHotelInfo, buildInfoItemForAirportInfo } from '../lib/functions';
import { useAuth } from '@/shared/stores/auth';
import { useClipboard } from '@/shared/model/useClipboard';
import { formatDateTime } from '@/shared/utils/dateFormat';
import { ADMIN_DELIVERY_STATUS_LABELS } from '@/shared/config/deliveryStatusLabels';
import { useApplicationEdit } from '../model/useApplicationEdit';
import CustomerInfoEditPanel from './CustomerInfoEditPanel.vue';
import HotelInfoEditPanel from './HotelInfoEditPanel.vue';
import MemoPanel from './MemoPanel.vue';

type AdminDeliveryStatus = components['schemas']['AdminDeliveryStatus'];

const props = defineProps<{
    applicationDetail?: ApplicationDetail;
    locationData?: LuggageLocationResponse;
    isLoading?: boolean;
    errorMessage?: string;
}>();

const emit = defineEmits<{
    'add-luggage': [];
    'remove-luggage': [];
    'photo-click': [photoUrl: string, photoType: 'reception' | 'arrangement'];
    'navigate-consent': [];
    'navigate-handover': [];
    'force-update-status': [];
    'update-completed': [];
    'memo-updated': [];
}>();

/**
 * 配送ステータスに応じた色を返す
 */
const getStatusColor = (status: AdminDeliveryStatus): string => {
    const colorMap: Record<AdminDeliveryStatus, string> = {
        applied: 'blue-grey',
        airport_counter_received: 'blue',
        in_transit: 'indigo',
        hotel_arrived: 'teal',
        hotel_checked: 'green',
        delivery_completed: 'success',
        cancelled: 'error',
        delayed: 'warning',
    };
    return colorMap[status] || 'grey';
};

const { role } = useAuth();

const userInfoItems = computed(() =>
    props.applicationDetail ? buildInfoItemForUserInfo(props.applicationDetail.customer_info) : []
);
const hotelInfoItems = computed(() =>
    props.applicationDetail ? buildInfoItemForHotelInfo(props.applicationDetail.hotel_info) : []
);
const airportInfoItems = computed(() =>
    props.applicationDetail
        ? buildInfoItemForAirportInfo(props.applicationDetail.airport_info, props.applicationDetail.counter_info)
        : []
);

const { showSnackbar: showCopySnackbar, copyToClipboard: copyApplicationId } = useClipboard();

/**
 * 申込情報を保存し、成功時にイベント発行を行う
 */
const handleSave = async () => {
    const success = await save();
    if (success) {
        emit('update-completed');
    }
};

// 荷物預かりが可能かどうか（申し込み直後のみ）
const canConsent = computed(() => {
    const status = props.applicationDetail?.delivery_status;
    return status === 'applied';
});

// 荷物受け渡しが可能かどうか（ホテル到着〜ホテル確認済みのみ）
const canHandover = computed(() => {
    const status = props.applicationDetail?.delivery_status;
    return status === 'hotel_arrived' || status === 'hotel_checked';
});

// 申込情報の編集が可能かどうか（受け渡し済み以外）
const canEditApplication = computed(() => {
    const status = props.applicationDetail?.delivery_status;
    return status !== 'delivery_completed';
});

// 申込編集フック
const {
    isEditMode,
    isLoading: isLoadingEdit,
    isUpdating,
    hotelItems,
    form,
    validationErrors,
    updateError,
    startEdit,
    cancelEdit,
    save,
} = useApplicationEdit(() => props.applicationDetail);
</script>

<template>
    <div class="application-detail-container">
        <div v-if="isLoading" class="d-flex justify-center align-center py-8">
            <v-progress-circular indeterminate color="primary" size="64" />
        </div>

        <v-alert v-else-if="errorMessage" type="error" variant="outlined" class="mb-4" :text="errorMessage" />

        <div v-else-if="applicationDetail" class="detail-content">
            <!-- 管理機能ボタンエリア（管理者のみ） -->
            <div v-if="role.isAdmin" class="admin-status-card mb-4">
                <!-- 配送ステータス＆ステータス変更 -->
                <v-card variant="outlined" class="pa-4">
                    <div class="d-flex flex-column align-center ga-3">
                        <div v-if="applicationDetail.customer_info?.delivery_status" class="d-flex align-center">
                            <v-icon class="me-2" color="primary">mdi-truck-delivery</v-icon>
                            <span class="text-body-1 font-weight-medium">配送ステータス: </span>
                            <v-chip
                                class="ms-2"
                                :color="getStatusColor(applicationDetail.customer_info.delivery_status)"
                            >
                                {{ ADMIN_DELIVERY_STATUS_LABELS[applicationDetail.customer_info.delivery_status] }}
                            </v-chip>
                        </div>
                        <v-btn color="warning" variant="outlined" @click="emit('force-update-status')">
                            ステータス変更
                        </v-btn>
                    </div>
                </v-card>
            </div>

            <!-- 申込情報変更 / 保存・キャンセル（管理者 + 空港スタッフ） -->
            <v-card
                v-if="(role.isAdmin || role.isAirport) && canEditApplication"
                variant="outlined"
                class="pa-4 d-flex align-center justify-center mb-4"
            >
                <!-- 編集モード時: 保存・キャンセルボタン -->
                <div v-if="isEditMode" class="d-flex ga-2">
                    <v-btn color="primary" variant="flat" :loading="isUpdating" @click="handleSave"> 保存 </v-btn>
                    <v-btn color="grey" variant="outlined" @click="cancelEdit"> キャンセル </v-btn>
                </div>
                <!-- 通常時: 申込情報変更ボタン -->
                <v-btn v-else color="primary" variant="outlined" :loading="isLoadingEdit" @click="startEdit">
                    申込情報変更
                </v-btn>
            </v-card>

            <!-- 更新エラー表示 -->
            <v-alert v-if="updateError" type="error" variant="tonal" class="mb-4" closable>
                {{ updateError }}
            </v-alert>

            <div class="panels-grid">
                <!-- 申込者パネル -->
                <CustomerInfoEditPanel
                    v-if="isEditMode"
                    v-model:reservation-holder-name="form.reservationHolderName"
                    v-model:user-name="form.userName"
                    v-model:relationship-with-guest="form.relationshipWithGuest"
                    v-model:email="form.email"
                    v-model:phone-number="form.phoneNumber"
                    v-model:is-phone-valid="form.isPhoneValid"
                    :errors="validationErrors"
                    class="panel-item"
                />
                <InfoPanel v-else title="申込者" class="panel-item" :items="userInfoItems" :default-open="true" />

                <!-- 発送元パネル -->
                <HotelInfoEditPanel
                    v-if="isEditMode"
                    v-model:hotel-id="form.hotelId"
                    v-model:check-in-date="form.checkInDate"
                    v-model:check-out-date="form.checkOutDate"
                    :hotel-items="hotelItems"
                    :errors="validationErrors"
                    class="panel-item"
                />
                <InfoPanel v-else title="発送元" class="panel-item" :items="hotelInfoItems" :default-open="true" />
                <InfoPanel title="届け先" class="panel-item" :items="airportInfoItems" :default-open="true" />
                <VExpansionPanel title="ユーザーQRコード" :default-open="true" class="panel-item">
                    <div class="d-flex flex-column align-center user-qr-panel-padding">
                        <QrCode
                            v-if="applicationDetail?.customer_info?.application_id"
                            :value="applicationDetail.customer_info.application_id"
                            alt="ユーザーQRコード"
                        />
                        <div v-if="applicationDetail?.customer_info?.application_id" class="d-flex align-center mt-2">
                            <p class="text-body-2 text-black mr-2">
                                {{ applicationDetail.customer_info.application_id }}
                            </p>
                            <v-btn
                                icon="mdi-content-copy"
                                variant="text"
                                size="small"
                                @click="copyApplicationId(applicationDetail.customer_info.application_id)"
                            />
                        </div>
                        <div v-else class="text-center">
                            <v-icon size="48" color="grey">mdi-qrcode</v-icon>
                            <p class="text-body-2 text-grey mt-2">QRコードを生成するには申し込みIDが必要です</p>
                        </div>

                        <div
                            v-if="applicationDetail?.customer_info?.application_id"
                            class="d-flex justify-center ga-6 mt-4"
                        >
                            <v-btn
                                v-if="role.isAdmin || role.isAirport"
                                color="primary"
                                :disabled="!canConsent"
                                @click="emit('navigate-consent')"
                            >
                                荷物預かり
                            </v-btn>
                            <v-btn
                                v-if="role.isAdmin || role.isHotel"
                                color="secondary"
                                :disabled="!canHandover"
                                @click="emit('navigate-handover')"
                            >
                                荷物受け渡し
                            </v-btn>
                        </div>
                    </div>
                </VExpansionPanel>
                <LuggageInfoPanel
                    :luggage-info="applicationDetail?.luggage_info"
                    :arrangement-photo-info="applicationDetail?.arrangement_photo_info"
                    :reception-photo-info="applicationDetail?.reception_photo_info"
                    :luggages="locationData?.luggages || []"
                    :default-open="true"
                    :show-manage-buttons="role.isAdmin || role.isAirport"
                    class="panel-item"
                    @add-luggage="emit('add-luggage')"
                    @remove-luggage="emit('remove-luggage')"
                    @photo-click="(url, type) => emit('photo-click', url, type)"
                />
                <LocationHistoryPanel
                    :delivery-locations="locationData?.delivery_locations || []"
                    :default-open="true"
                    class="panel-item"
                />

                <!-- 管理メモパネル（受領サインの前・独立して編集可能） -->
                <MemoPanel
                    :application-id="applicationDetail.customer_info.application_id"
                    :memo="applicationDetail.memo"
                    class="panel-item"
                    @updated="emit('memo-updated')"
                />

                <VExpansionPanel
                    v-if="applicationDetail?.sign_info?.handover_sign_url"
                    title="受領サイン"
                    :default-open="true"
                    class="panel-item"
                >
                    <div class="pa-4">
                        <div class="sign-section">
                            <p class="text-subtitle-2 mb-2">受け渡しサイン</p>
                            <v-img
                                :src="applicationDetail.sign_info.handover_sign_url"
                                alt="受け渡しサイン"
                                class="sign-image"
                                max-height="200"
                                contain
                            />
                            <p
                                v-if="applicationDetail?.sign_info?.handover_sign_timestamp"
                                class="text-caption text-grey mt-1"
                            >
                                {{ formatDateTime(applicationDetail.sign_info.handover_sign_timestamp) }}
                            </p>
                        </div>
                    </div>
                </VExpansionPanel>
            </div>
        </div>

        <div v-else class="text-center py-8">
            <v-icon size="64" color="grey-lighten-1" class="mb-2">mdi-file-search</v-icon>
            <p class="text-body-1 text-grey">申し込みデータが見つかりません</p>
        </div>

        <v-snackbar v-model="showCopySnackbar" :timeout="2000" location="top"> コピーしました </v-snackbar>
    </div>
</template>

<style scoped>
.application-detail-container {
    padding: 16px;
    margin: 0 auto;
}

.detail-content {
    width: 100%;
}

.panels-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
}

/* スマホレイアウトでユーザーqrコード部分だけ大きくなって崩れるので調節 */
.user-qr-panel-padding {
    padding: 16px 0;
}

/* レスポンシブレイアウト */
@media (min-width: 768px) {
    .panels-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (min-width: 1024px) {
    .application-detail-container {
        padding: 24px;
    }

    .panels-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
    }

    .user-qr-panel-padding {
        padding: 16px;
    }
}

.panel-item {
    height: fit-content;
}

/* パネルのアニメーション */
.panel-item {
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
}

.panel-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.sign-image {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: white;
}

.status-section {
    border-radius: 8px;
}

/* 管理機能ステータスカード */
.admin-status-card {
    display: block;
}
</style>
